import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getDistribucionTalentosThunk } from "../../../store/reducers/dashboard/distribucionTalentos/distribucionTalentos.thunks";

/**
 * Asigna un color por barra según la posición:
 *  Top 5  → verde   (predominancia alta)
 *  Bottom 5 → rojo  (predominancia baja)
 *  Resto  → azul primario
 */
const buildColors = (sortedData) =>
    sortedData.map((_, i) => {
        if (i < 5) return "#45B369";          // success-600
        if (i >= sortedData.length - 5) return "#E8534A"; // danger-600
        return "#4A90E2";                     // primary-600
    });

const CardDistribucionTalentos = () => {
    const dispatch = useDispatch();
    const { distribucionTalentos, loading, error } = useSelector(
        (state) => state.distribucionTalentos
    );
    const { escuela, plantel } = useSelector((state) => state.dashboardFilters);

    useEffect(() => {
        dispatch(getDistribucionTalentosThunk());
    }, [dispatch, escuela, plantel]);

    // Ordenar de mejor (orden más bajo) a peor (orden más alto)
    const sorted = [...distribucionTalentos].sort((a, b) => a.orden - b.orden);

    // Invertir la escala: score = maxOrden - orden + 1 → barra más alta = mejor talento
    const maxOrden = sorted.length > 0 ? sorted[sorted.length - 1].orden : 35;
    const scores = sorted.map((t) => maxOrden - t.orden + 1);
    const labels = sorted.map((t) => t.talento);
    const colors = buildColors(sorted);

    const chartOptions = {
        chart: {
            type: "bar",
            toolbar: { show: false },
            fontFamily: "inherit",
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                distributed: true,
                columnWidth: "60%",
            },
        },
        colors,
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: {
            categories: labels,
            labels: {
                rotate: -60,
                style: { fontSize: "10px" },
            },
            tooltip: { enabled: false },
        },
        yaxis: {
            show: false,
        },
        tooltip: {
            custom: ({ dataPointIndex }) => {
                const talento = sorted[dataPointIndex];
                return `
          <div style="padding:8px 12px;font-size:12px;line-height:1.6">
            <strong>${talento.talento}</strong><br/>
            Posición: <strong>#${talento.orden}</strong><br/>
            Total evaluaciones: <strong>${talento.total}</strong>
          </div>`;
            },
        },
        grid: {
            show: false,
        },
    };

    const chartSeries = [{ name: "Predominancia", data: scores }];

    return (
        <div className="col-6">
            <div className="card h-100">
                <div className="card-header">
                    <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
                        <h6 className="mb-0 fw-bold text-lg">
                            <Icon
                                icon="solar:chart-2-bold"
                                className="me-8 text-primary-600"
                            />
                            Distribución de Talentos
                        </h6>
                        <div className="d-flex align-items-center gap-16">
                            <span className="d-flex align-items-center gap-6 text-sm text-secondary-light">
                                <span
                                    className="w-12-px h-12-px rounded-circle"
                                    style={{ backgroundColor: "#45B369" }}
                                />
                                Mayor predominancia
                            </span>
                            <span className="d-flex align-items-center gap-6 text-sm text-secondary-light">
                                <span
                                    className="w-12-px h-12-px rounded-circle"
                                    style={{ backgroundColor: "#4A90E2" }}
                                />
                                Intermedio
                            </span>
                            <span className="d-flex align-items-center gap-6 text-sm text-secondary-light">
                                <span
                                    className="w-12-px h-12-px rounded-circle"
                                    style={{ backgroundColor: "#E8534A" }}
                                />
                                Menor predominancia
                            </span>
                        </div>
                    </div>
                </div>

                <div className="card-body p-24">
                    {loading && (
                        <div className="d-flex justify-content-center align-items-center py-40">
                            <span className="text-secondary-light">
                                Cargando distribución...
                            </span>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger">
                            Error cargando la distribución de talentos
                        </div>
                    )}

                    {!loading && !error && sorted.length > 0 && (
                        <>
                            <ReactApexChart
                                options={chartOptions}
                                series={chartSeries}
                                type="bar"
                                height={320}
                            />
                            <p className="text-sm text-secondary-light mt-16 mb-0 text-center">
                                Refleja cómo se han posicionado los talentos a lo largo de
                                <strong> todas las sesiones de evaluación registradas</strong>.
                                Cada barra representa la posición promedio que ocupa un talento dentro
                                del conjunto evaluado.
                                <br />
                                <strong>
                                    Las barras más altas indican talentos con mayor predominancia colectiva,
                                    mientras que las más bajas reflejan menor frecuencia en los primeros lugares.
                                </strong>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CardDistribucionTalentos;
